import { useMsal } from '@azure/msal-react'
import { faGreaterThan, faRefresh } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import React, { useCallback, useEffect, useState } from 'react'
import { getInactiveDevices } from '../../data/admin.service'
import { InactiveDevice } from '../../models/Admin/InactiveDevice'
import { COLORS } from '../../utils/constants'

const InactiveDevices: React.FC = () => {
  const { instance } = useMsal()
  const [inactiveDevices, setInactiveDevices] = useState<InactiveDevice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [daysInactive, setDaysInactive] = useState(7)
  const [lastQueriedDays, setLastQueriedDays] = useState(7)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const fetchInactiveDevices = useCallback(
    async (days: number) => {
      try {
        const devices = await getInactiveDevices(instance, days)
        setInactiveDevices(devices)
        setLastQueriedDays(days)
      } catch (error) {
        console.error('Error fetching inactive devices:', error)
        notifications.show({
          title: 'Error',
          message: 'Failed to fetch inactive devices. Please try again.',
          color: 'red',
          autoClose: true,
        })
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    },
    [instance],
  )

  useEffect(() => {
    fetchInactiveDevices(7)
  }, [fetchInactiveDevices])

  const handleDaysChange = (value: string | number) => {
    const newDays = typeof value === 'string' ? parseInt(value) : value
    if (newDays && newDays > 0) {
      setDaysInactive(newDays)
    }
  }

  const handleRefresh = () => {
    setIsRefreshing(true)
    fetchInactiveDevices(daysInactive)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <Box py={24}>
        <Group justify='center' mt={50}>
          <Loader size='lg' />
        </Group>
      </Box>
    )
  }

  return (
    <Box py={24}>
      <Group justify='space-between' mb={24}>
        <Title>Inactive Devices</Title>
        <Group align='flex-end'>
          <NumberInput
            label='Inactive Days:'
            placeholder='Enter days'
            value={daysInactive}
            onChange={handleDaysChange}
            min={1}
            max={365}
            leftSection={<FontAwesomeIcon icon={faGreaterThan} />}
            style={{ width: 150 }}
          />
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            leftSection={
              isRefreshing ? (
                <Loader size='xs' />
              ) : (
                <FontAwesomeIcon icon={faRefresh} />
              )
            }
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Group>
      </Group>

      {inactiveDevices.length === 0 ? (
        <Paper withBorder p={36} mt={32} bg={COLORS.LightGray} radius={16}>
          <Text ta='center' size='lg'>
            No devices have been inactive for {lastQueriedDays} or more days
          </Text>
        </Paper>
      ) : (
        <>
          {/* Desktop Table View */}
          <Box visibleFrom='md'>
            <Paper withBorder radius={16} p={0} style={{ overflow: 'hidden' }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Device</Table.Th>
                    <Table.Th>Location</Table.Th>
                    <Table.Th>Organization</Table.Th>
                    <Table.Th>Last Request</Table.Th>
                    <Table.Th>Days Inactive</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {inactiveDevices.map((device) => (
                    <Table.Tr
                      key={device.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        (window.location.href = `/devices/${device.id}`)
                      }
                    >
                      <Table.Td>
                        <Text fw={600}>{device.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{device.locationName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{device.organizationName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>
                          {formatDate(device.lastRequestDate)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          fw={600}
                          c={
                            device.daysInactive > 30
                              ? 'red'
                              : device.daysInactive > 14
                              ? 'orange'
                              : 'green'
                          }
                        >
                          {device.daysInactive} days
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Box>

          {/* Mobile Card View */}
          <Box hiddenFrom='md'>
            <Stack gap='md'>
              {inactiveDevices.map((device) => (
                <Card
                  key={device.id}
                  shadow='sm'
                  padding='md'
                  radius='md'
                  withBorder
                  style={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  }}
                  styles={{
                    root: {
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                    },
                  }}
                  onClick={() =>
                    (window.location.href = `/devices/${device.id}`)
                  }
                >
                  <Stack gap='sm'>
                    <Text fw={600} size='lg'>
                      {device.name}
                    </Text>

                    <Group justify='space-between'>
                      <Text size='sm' c='dimmed'>
                        Location:
                      </Text>
                      <Text size='sm' ta='right' style={{ flex: 1 }}>
                        {device.locationName}
                      </Text>
                    </Group>

                    <Group justify='space-between'>
                      <Text size='sm' c='dimmed'>
                        Organization:
                      </Text>
                      <Text size='sm' ta='right' style={{ flex: 1 }}>
                        {device.organizationName}
                      </Text>
                    </Group>

                    <Group justify='space-between'>
                      <Text size='sm' c='dimmed'>
                        Last Request:
                      </Text>
                      <Text size='sm' ta='right' style={{ flex: 1 }}>
                        {formatDate(device.lastRequestDate)}
                      </Text>
                    </Group>

                    <Group justify='space-between' mt='xs'>
                      <Text size='sm' c='dimmed'>
                        Days Inactive:
                      </Text>
                      <Text
                        fw={700}
                        size='lg'
                        c={
                          device.daysInactive > 30
                            ? 'red'
                            : device.daysInactive > 14
                            ? 'orange'
                            : 'green'
                        }
                      >
                        {device.daysInactive} days
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Box>
  )
}

export default InactiveDevices
